import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { CrossingClassMenuProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

/**
 * CrossingClassMenu — desplegable para seleccionar la clase de documento hijo
 * al generar un cruce desde el documento actual.
 */
export function CrossingClassMenu({
  options,
  onSelect,
  disabled = false,
  className,
}: CrossingClassMenuProps) {
  const { t } = useMasterDocumentTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (id: string) => {
    setOpen(false);
    onSelect(id);
  };

  return (
    <div ref={containerRef} className={clsx('relative inline-block', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
          'bg-primary-inverse-background text-primary-inverse-content hover:bg-primary-custom-700',
          'dark:bg-dark-bg-inverse dark:text-dark-content-inverse',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        {t('master_document.crossing.generateDocument')}
        <ChevronDownIcon
          className={clsx('w-3.5 h-3.5 transition-transform duration-150', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className={clsx(
            'absolute right-0 mt-1 z-50 min-w-[200px] py-1',
            'bg-bg-primary dark:bg-dark-bg-primary',
            'rounded-xl border border-border-primary dark:border-dark-border-primary',
            'shadow-[0_4px_16px_rgba(0,0,0,.12)] dark:shadow-[0_4px_16px_rgba(0,0,0,.4)]',
          )}
        >
          {options.map((option) => (
            <li key={option.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => handleSelect(option.id)}
                className={clsx(
                  'w-full text-left px-3 py-2 text-sm transition-colors',
                  'text-content-primary dark:text-dark-content-primary',
                  'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                )}
              >
                <span className="block">{option.label}</span>
                {option.description && (
                  <span className="block text-xs text-content-secondary dark:text-dark-content-secondary mt-0.5">
                    {option.description}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
